import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProductReviewState } from "./productReview.state";
import { getProductReviews, getProductReviewsList, getReviewVisuals, performReviewAction } from "./productReview.thunk";
import {
  ProductReviewData,
  ReviewFilters,
  ReviewsListData,
} from "./productReview.types";

const initialState: ProductReviewState = {
  data: null,
  reviewsList: null,
  status: "idle",
  reviewsListStatus: "idle",
  error: null,
  reviewVisuals: null,
  reviewVisualsStatus: "idle",
  pendingFilters: {},
  appliedFilters: {},
  currentProductSlug: null,
  showFilterSidebar: false,
  openFilterDropdowns: null,
};

export const productReviewSlice = createSlice({
  name: "productReview",
  initialState,
  reducers: {
    resetProductReview: (state) => {
      state.data = null;
      state.reviewsList = null;
      state.status = "idle";
      state.reviewsListStatus = "idle";
      state.reviewVisuals = null;
      state.reviewVisualsStatus = "idle";
      state.error = null;
      state.pendingFilters = {};
      state.appliedFilters = {};
      state.currentProductSlug = null;
      state.showFilterSidebar = false;
      state.openFilterDropdowns = null;
    },
    resetReviewVisuals: (state) => {
      state.reviewVisuals = null;
      state.reviewVisualsStatus = "idle";
    },
    /** SSR/ISR verisini mount'ta Redux'a yükler — status "success" olduğu için
     *  aşağıdaki "status === idle" fetch effect'i tetiklenmez, çift istek olmaz.
     *  Filtresiz ilk sayfaya karşılık gelir; kullanıcı filtre uygulayınca
     *  normal thunk'lar devralır. */
    setInitialProductReview: (
      state,
      action: PayloadAction<{
        slug: string;
        data: ProductReviewData | null;
        reviewsList: ReviewsListData | null;
      }>,
    ) => {
      state.currentProductSlug = action.payload.slug;
      if (action.payload.data) {
        state.data = action.payload.data;
        state.status = "success";
      }
      if (action.payload.reviewsList) {
        state.reviewsList = action.payload.reviewsList;
        state.reviewsListStatus = "success";
      }
      state.error = null;
    },
    setCurrentProductSlug: (state, action: PayloadAction<string>) => {
      // Slug değiştiğinde status'u idle'a çek ki yeni fetch tetiklensin
      if (state.currentProductSlug !== action.payload) {
        state.currentProductSlug = action.payload;
        state.status = "idle";
        state.reviewsListStatus = "idle";
      }
    },
    setPendingFilters: (state, action: PayloadAction<ReviewFilters>) => {
      state.pendingFilters = action.payload;
    },
    setAppliedFilters: (state, action: PayloadAction<ReviewFilters>) => {
      state.appliedFilters = action.payload;
      state.reviewsList = null;
      state.reviewsListStatus = "idle";
    },
    syncFiltersFromUrl: (state, action: PayloadAction<ReviewFilters>) => {
      state.pendingFilters = action.payload;
      state.appliedFilters = action.payload;
    },
    toggleRatingFilter: (state, action: PayloadAction<number>) => {
      const rating = action.payload;
      const currentRatings = state.pendingFilters.rating || [];
      const newRatings = currentRatings.includes(rating)
        ? currentRatings.filter(r => r !== rating)
        : [...currentRatings, rating];
      
      state.pendingFilters = {
        ...state.pendingFilters,
        rating: newRatings.length > 0 ? newRatings : undefined,
      };
    },
    toggleTopicFilter: (state, action: PayloadAction<string>) => {
      const topic = action.payload;
      const currentTopics = state.pendingFilters.topic || [];
      const newTopics = currentTopics.includes(topic)
        ? currentTopics.filter(t => t !== topic)
        : [...currentTopics, topic];
      
      state.pendingFilters = {
        ...state.pendingFilters,
        topic: newTopics.length > 0 ? newTopics : undefined,
      };
    },
    setSellerFilter: (state, action: PayloadAction<number | undefined>) => {
      state.pendingFilters = {
        ...state.pendingFilters,
        seller_id: action.payload,
      };
    },
    setSearchFilter: (state, action: PayloadAction<string | undefined>) => {
      state.pendingFilters = {
        ...state.pendingFilters,
        search: action.payload,
      };
    },
    setSortFilter: (state, action: PayloadAction<string | undefined>) => {
      state.pendingFilters = {
        ...state.pendingFilters,
        sort: action.payload,
      };
    },
    clearAllFilters: (state) => {
      state.pendingFilters = {};
    },
    clearRatingFilter: (state) => {
      state.pendingFilters = {
        ...state.pendingFilters,
        rating: undefined,
      };
    },
    clearTopicFilter: (state) => {
      state.pendingFilters = {
        ...state.pendingFilters,
        topic: undefined,
      };
    },
    setShowFilterSidebar: (state, action: PayloadAction<boolean>) => {
      state.showFilterSidebar = action.payload;
    },
    setOpenFilterDropdown: (state, action: PayloadAction<string | null>) => {
      state.openFilterDropdowns = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get product reviews (page data)
      .addCase(getProductReviews.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getProductReviews.fulfilled, (state, action) => {
        state.status = "success";
        state.data = action.payload.data;
      })
      .addCase(getProductReviews.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload ?? "Değerlendirmeler yüklenirken bir hata oluştu.";
      })
      // Get product reviews list
      .addCase(getProductReviewsList.pending, (state, action) => {
        const isFirstPage = !action.meta.arg.filters?.page || action.meta.arg.filters?.page === 1;
        
        if (isFirstPage) {
          state.reviewsList = null;
        }
        
        state.reviewsListStatus = "loading";
        state.error = null;
      })
      .addCase(getProductReviewsList.fulfilled, (state, action) => {
        state.reviewsListStatus = "success";
        const newReviews = action.payload.data.reviews;
        const currentPage = action.payload.data.pagination.current_page;
        
        // If page 1, replace. Otherwise, append
        if (currentPage === 1) {
          state.reviewsList = action.payload.data;
        } else {
          state.reviewsList = {
            ...action.payload.data,
            reviews: [
              ...(state.reviewsList?.reviews || []),
              ...newReviews
            ]
          };
        }
      })
      .addCase(getProductReviewsList.rejected, (state, action) => {
        state.reviewsListStatus = "error";
        state.error = action.payload ?? "Değerlendirmeler yüklenirken bir hata oluştu.";
      })
      // Get review visuals
      .addCase(getReviewVisuals.pending, (state, action) => {
        const isFirstPage = !action.meta.arg.page || action.meta.arg.page === 1;
        
        if (isFirstPage) {
          state.reviewVisuals = null;
        }
        
        state.reviewVisualsStatus = "loading";
        state.error = null;
      })
      .addCase(getReviewVisuals.fulfilled, (state, action) => {
        state.reviewVisualsStatus = "success";
        const newItems = action.payload.data.items;
        const currentPage = action.payload.data.pagination.current_page;
        
        // If page 1, replace. Otherwise, append
        if (currentPage === 1) {
          state.reviewVisuals = action.payload.data;
        } else {
          state.reviewVisuals = {
            ...action.payload.data,
            items: [
              ...(state.reviewVisuals?.items || []),
              ...newItems
            ]
          };
        }
      })
      .addCase(getReviewVisuals.rejected, (state, action) => {
        state.reviewVisualsStatus = "error";
        state.error = action.payload ?? "Görseller yüklenirken bir hata oluştu.";
      })
      // Review action (like/complaint)
      .addCase(performReviewAction.fulfilled, (state, action) => {
        // Update reviews list if available
        if (state.reviewsList) {
          const review = state.reviewsList.reviews.find(
            (r) => r.id === action.payload.reviewId
          );
          if (review) {
            review.like_count = action.payload.like_count;
            review.user_action = action.payload.user_action;
          }
        }
      });
  },
});

export const { 
  resetProductReview,
  resetReviewVisuals,
  setInitialProductReview,
  setCurrentProductSlug,
  setPendingFilters,
  setAppliedFilters,
  syncFiltersFromUrl,
  toggleRatingFilter,
  toggleTopicFilter,
  setSellerFilter,
  setSearchFilter,
  setSortFilter,
  clearAllFilters,
  clearRatingFilter,
  clearTopicFilter,
  setShowFilterSidebar,
  setOpenFilterDropdown,
} = productReviewSlice.actions;

export default productReviewSlice.reducer;